import { EntityPagingInfo } from '../common/entity.paging.info';
import { EntityView, EntityViewRequestOptions, EntityViewSort } from './entity.view';
import { EntityViewFilter, EntityFilterValue } from './entity.view.filter';
import { EntityAdvancedFilterConditionLoose, EntityAdvancedFilterGroup, EntityAdvancedFilterLogicalOperator, EntityAdvancedFilterNode, EntityAdvancedFilterTargetStrategy } from './entity.view.advanced.filter';
import { EntityViewMetadata } from './entity.view.metadata';
import { EntityItemsRange } from './entity.view.calendar';
import { MetadataEntityData } from '../../../metadata/entity/decorators/model/metadata.entity.data';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { DEFAULT_PAGE_SIZE, UNPAGED_MAX_RECORDS } from '../../../utilities/paging.utility';
import { DomainType } from '../../../utilities/domain.utility';
import { EntityAdvancedFilterOperator, EntityViewType } from './enums';
import { MetadataEntityRecordScope } from '../../../metadata/entity/decorators/model/metadata.entity.record.scope';
import * as Enumerable from 'linq';

export interface EntityViewRequest {
    entity: ActivityEntityName;
    currentUserId?: string;
    domainType?: DomainType;
}

export interface EntityViewFavoriteRequest {
    viewId: string;
}


export interface EntityHeaderRequest {
    viewId?: string;
    isEmbedded?: boolean;
    variant?: string;
    overrideMetadataEntity?: ActivityEntityName;
    overrideViewEntity?: ActivityEntityName;
    /** Enum grain or absolute embedded path (e.g. `workOrders.codes`). */
    recordScope?: MetadataEntityRecordScope | string;
    /** An unsaved definition being previewed in settings, for strategies whose metadata depends on it (queue drafts). */
    preview?: Record<string, unknown>;

    /** @deprecated - This is enforced by by the data strategy */
    domainType?: DomainType;
}

export interface EntityItemsRequestBase<T = any> {
    viewId?: string;
    parentId?: string;
    parentEntity?: ActivityEntityName;
    companyIds?: string[];
    search?: string;
    filters?: EntityViewFilter<T>[];
    advancedFilter?: EntityAdvancedFilterGroup<T>;
    advancedFilterStrategy?: EntityAdvancedFilterTargetStrategy;
    entityIds?: Record<ActivityEntityName, string | string[]>;
    sort?: EntityViewSort,
    page?: number;
    pageSize?: number;
    /**
     * Keyset cursor for the page being asked for — the `nextCursor` the PREVIOUS page landed with.
     *
     * Page 0 sends none. A keyset reader resumes from an opaque position rather than re-walking
     * `page * pageSize` rows, which is what keeps a deep page as cheap as the first one.
     */
    after?: string;
    /**
     * Snapshot token minted by the first page of a keyset walk and echoed on every later page.
     *
     * Without it each page is a fresh read of a moving collection, so a row inserted mid-walk shifts
     * the window and the viewer sees a duplicate or a hole. The token pins every page of one walk to
     * the same snapshot; it is cleared whenever paging restarts.
     */
    readToken?: string;
    /**
     * Single-shot fetch: no client paging ($skip forced to 0). Source page units are capped at
     * UNPAGED_MAX_RECORDS (5000); parent units expanded after the cap can produce more leaf rows.
     */
    unpaged?: boolean;
    /**
     * Visible date range (Calendar display). Server narrows to records whose `view.calendar.startField`
     * (and `endField`, when set) intersect the range. Sent with `unpaged` — a calendar renders every
     * event in range, never a page of them.
     */
    range?: EntityItemsRange;
    /**
     * Skip the count/$facet total branch in supporting v2 strategies. Use on page-only navigation when the client already has
     * total from an earlier request (view/filter/group/pageSize change). Board sends true when page > 0.
     */
    skipCount?: boolean;
    /**
     * Client/view group field for supporting v2 strategies. Overrides view.table.groupBy for server paging grain
     * (parent-complete pages when grouping by job on a stages board).
     */
    groupBy?: string | null;
    /** Optional nested group field; considered with groupBy for shallowest parent page unit. */
    subGroupBy?: string | null;
    /**
     * One server group's key, as its group row carried it. With `groupBy` on a view whose table declares
     * `serverGroups`, the read answers that group's leaf rows (paged by page/pageSize) instead of group rows.
     */
    groupKey?: string | null;
    isEmbedded?: boolean;
    variant?: string;
    overrideViewEntity?: ActivityEntityName;
    /** Enum grain or absolute embedded path (e.g. `workOrders.codes`). */
    recordScope?: MetadataEntityRecordScope | string;
    /** Timezone for formatting date/time values (e.g., 'Australia/Sydney', 'UTC'). Used by audit logs. */
    timezone?: string;
    /** Unsaved request-scoped preview data used by editable board settings. */
    preview?: Record<string, any>;
    /** Selected single-select preset (tab) id. Resolved server-side to its filters. */
    presetId?: string;
    /** Request per-preset counts for the view's presets (ListDetail tab badges). */
    includePresetCounts?: boolean;
    /** Selected segment filter (tile) id — entity-level, from metadata.segmentFilters. Resolved
     *  server-side to its filters. Counts are NOT carried here; see EntitySegmentCountsRequest. */
    segmentId?: string;

    /** @deprecated - This is enforced by by the data strategy */
    domainType?: DomainType;
}

export interface EntityItemsRequestV1<T = any> extends EntityItemsRequestBase<T> {
    version?: 1;
    skipProjection?: never;
}

export interface EntityItemsRequestV2<T = any> extends EntityItemsRequestBase<T> {
    version: 2;
    /** Skip field projection to return all fields. Used when editing/creating views so new columns have data. */
    skipProjection?: boolean;
}

export type EntityItemsRequestV2Input<T = any> = EntityItemsRequestBase<T> & { skipProjection?: boolean };

export type EntityItemsRequest<T = any> = EntityItemsRequestV1<T> | EntityItemsRequestV2<T>;

export interface EntityFilterRequest {
    viewId?: string;
    parentId?: string;
    filterName?: string;
}


export interface EntityHeaderResponse<T = any> {
    metadata?: MetadataEntityData,
    views?: EntityView<T>[];
    view?: EntityView<T>;
}

/**
 * Row-group facts a page cannot state for itself.
 *
 * Only the no-value TAIL needs one. Real groups are whole on the page by construction (row-group
 * paging admits a group only when its first row falls in the window, then pads to the group's end),
 * so a lane's page count IS its total. The tail is deliberately exempt from that padding — it can be
 * the entire system — so it spans pages and a lane header reading its page count alone would claim
 * "47" for a group of 24,997.
 */
export interface EntityItemsGroups {
    /** The no-value group. Present only when row-group paging ran. */
    tail?: {
        /** Total rows in the no-value group across every page, not just this one. */
        rows: number;
    };
}

/**
 * The window a period-bounded read actually ran over, echoed when the SERVER chose it.
 *
 * A board that declares a period normally sends its range, and then this is absent — the client
 * already knows. It is present only when the request arrived unbounded and the server fell back to
 * tenant policy, which is the one case where the rows on screen and the label above them could
 * otherwise describe different stretches of time. The board adopts it once, as its opening period,
 * and from then on the pill is authoritative and every request carries the range.
 *
 * Inclusive local calendar dates (`yyyy-MM-dd`), the same spelling the period predicate travels in.
 */
export interface EntityItemsPeriod {
    from: string;
    to: string;
    /** The tenant-policy preset the window came from, when a named one did. */
    preset?: string;
}

/**
 * One group row of a `serverGroups` read. The page is made of these and `total` counts groups, so the
 * pager pages groups; a group's leaves are read separately with `groupKey`, never shipped here.
 */
export interface EntityItemsGroupRow<G = unknown> {
    groupKey: string;
    /** Exact leaf count, from the reader's own index rather than a walk. */
    count: number;
    /** Entity-owned header facts; only that entity's group row renderer reads them. */
    group: G;
}

export interface EntityItemsResponse<T = any> extends EntityPagingInfo {
    /** @deprecated */
    metadata?: MetadataEntityData,

    /** @deprecated */
    views?: EntityView<T>[];

    /** @deprecated */
    view?: EntityView<T>;
    items: T[];
    /** Present only on a `serverGroups` read at group level; `items` is then empty. */
    groupRows?: EntityItemsGroupRow[];
    /** Count per preset id, computed under base scope + dropdown filters (excludes the active preset). */
    presetCounts?: Record<string, number>;
    /** Complete base-filtered counts, before segment selection or paging; used when metadata declares `countsSource: 'items'`. */
    segmentCounts?: Record<string, number>;
    /** Row-group totals a swimlane header cannot derive from the page. Absent unless row-group paging ran. */
    groups?: EntityItemsGroups;
    /** Present only when the server resolved the read's window itself — see `EntityItemsPeriod`. */
    period?: EntityItemsPeriod;
    /** Items omitted from this source page because they cannot be acted on. Not a whole-result total. */
    excludedItems?: { count: number; reasons: string[] };
}

/** Scope-only subset for segment counts: no paging/sort and no active segment — counts are
 *  segment-independent (each facet branch computes its own) and unaffected by paging, so the
 *  request identity stays stable across page/sort/tile changes (count queries never refetch). */
export type EntitySegmentCountsRequest<T = any> = Omit<EntityItemsRequestBase<T>, 'sort' | 'page' | 'pageSize' | 'preview' | 'presetId' | 'includePresetCounts' | 'segmentId'>;

export interface EntitySegmentCountsResponse {
    /** Count per segment id, computed under base scope + dropdown filters. */
    counts: Record<string, number>;
    /** Server-side count cap. A count at this limit may have additional matches. */
    countLimit?: number;
}

export interface EntityCommandRequest<T = any> {
    commandId: string;
    data: T;
}

export interface EntityEventsRequest {
    entityId?: string;
    runId?: string;
    streamId?: string;
    types?: string[] | string;
}

export interface EntityEvent<T = any> {
    type: string;
    entity: ActivityEntityName;
    entityId?: string;
    runId?: string;
    timestamp?: Date;
    payload?: T;
}

export function emptyEntityHeaderResponse() {
    return <EntityHeaderResponse>{
        metadata: null,
        views: [],
        items: [],
    };
}

export function emptyEntityItemsResponse() {
    return {
        total: 0,
        page: 0,
        pageSize: 0,
        views: [],
        items: [],
        metadata: null,
        presetCounts: {},
    } as EntityItemsResponse;
}

/**
 * Merge a reader's page onto the empty-response seed WITHOUT resurrecting a `total` the page omitted.
 *
 * WBS-11112 — THE BUG THIS EXISTS TO PREVENT. `emptyEntityItemsResponse()` seeds `total: 0`, and a
 * forecast reader (Upcoming Work, a twelve-month Visits window) deliberately sends no `total` at all —
 * it reports `hasMore` instead, because counting the window is the dominant cost of the read. A plain
 * spread of the page over that seed therefore leaves `total: 0` standing: `EntityPagingInfo` tells
 * consumers to treat a numeric `total` as authoritative, so a board with rows would paginate as empty.
 *
 * Deleting the key is what makes "absent" survive the merge, and it must be the KEY that goes rather
 * than the value — `total: undefined` is still an own property, and a consumer testing `'total' in page`
 * or a serialiser that emits nulls would see it.
 *
 * A page that DOES carry a `total` (Visits History, and every non-forecast entity) keeps it untouched,
 * which is why this is safe to use anywhere a page is merged onto the seed.
 */
export function mergeEntityItemsPage<T>(page: Partial<EntityItemsResponse<T>>, rest: Partial<EntityItemsResponse<T>> = {}): EntityItemsResponse<T> {

    const merged = { ...emptyEntityItemsResponse(), ...page, ...rest } as EntityItemsResponse<T>;
    if (page.total === undefined) delete merged.total;

    return merged;
}

export function emptyEntityItemResponse() {
    return {
        item: null,
        metadata: null,
    };
}

export function getEntityItemsRequestFromView(view: EntityView): EntityItemsRequest {
    return {
        filters: view.filters,
        advancedFilter: view.advancedFilter,
        advancedFilterStrategy: view.advancedFilterStrategy,
        sort: view.sort,
        page: 0,
        pageSize: view?.pageSize ?? DEFAULT_PAGE_SIZE
    }
}

/** Comparable allow-list identity. A scalar and a one-element array are the same value. */
function allowListKeys(value: unknown): string[] | null {

    if (value == null || value === '') return null;

    const list = Array.isArray(value) ? value : [value];
    if (!list.length) return null;

    const keys: string[] = [];
    for (const item of list) {
        if (item == null || item === '') return null;
        if (typeof item !== 'object') {
            keys.push(String(item));
            continue;
        }
        const id = (item as { _id?: unknown })._id;
        if (id == null) return null;
        keys.push(String(id));
    }

    return keys.sort();
}

function sameKeyList(left: string[], right: string[]): boolean {

    return left.length === right.length && left.every((key, index) => key === right[index]);
}

/**
 * A filter-bar chip on a pill-owned field must AND with the pill. Repeating the pill, or repeating
 * the default segment's allow-list, must not: that leftover is All's live statuses, and ANDing it
 * with Billed or Closed empties the list while the pill still counts the terminal status.
 */
function shouldAndSegmentChip(user: EntityViewFilter, segmentFilter: EntityViewFilter, defaultFilter: EntityViewFilter | undefined): boolean {

    const userKeys = allowListKeys(user.value);
    const segmentKeys = allowListKeys(segmentFilter.value);
    if (!userKeys || !segmentKeys) return false;
    if (sameKeyList(userKeys, segmentKeys)) return false;

    const defaultKeys = defaultFilter ? allowListKeys(defaultFilter.value) : null;
    if (defaultKeys && sameKeyList(userKeys, defaultKeys)) return false;

    return true;
}

/**
 * Builds the query-oriented effective request from view, request, preset, and segment state. It
 * normalizes unpaged limits and forwards grouping hints; the data strategy resolves paging grain.
 */
export function getEffectiveEntityItemsRequest(metadata: EntityViewMetadata, view: EntityView<any>, request: EntityItemsRequest<any>): EntityItemsRequest {

    const getSafeId = (filter: EntityViewFilter) => {
        const path = filter?.fieldId ?? filter._id;
        const safeId = path.endsWith('._id')
            ? path.replace('._id', '')
            : path;
        return safeId;
    }

    const activePreset = (view?.presetConfig?.items ?? []).find(p => p._id === request?.presetId);
    const presetFilters = activePreset?.filters ?? [];

    // Segment filters are entity-owned (metadata.segmentFilters), unlike presets which ride the
    // view. A pill replaces the same field on the dropdown AND the view — All's leftover live-status
    // allow-list must not AND with Billed or Closed (Cancelled) or the list empties while counts, which
    // fan out each pill, still show the terminal-status totals. Tenant All views can still carry that allow-list.
    // A different chip (Status is any of Draft) is not that leftover: the pill keeps the slot and the
    // chip is ANDed beside it, or the dictionary's last write would throw the chip away and the list
    // would stay on the pill's whole allow-list.
    const segmentItems = metadata?.metadata?.segmentFilters?.items ?? [];
    const activeSegment = segmentItems.find(s => s._id === request?.segmentId);
    const defaultSegment = segmentItems.find(item => item.isDefault) ?? segmentItems[0];
    const segmentFilters = activeSegment?.filters ?? [];
    const segmentFieldIds = new Set(segmentFilters.map(getSafeId));
    const userByField = new Map((request?.filters ?? []).map(filter => [getSafeId(filter), filter]));
    const segmentByField = new Map(segmentFilters.map(filter => [getSafeId(filter), filter]));
    const defaultByField = new Map((defaultSegment?.filters ?? []).map(filter => [getSafeId(filter), filter]));

    const mergedRequestFilters = [...(request?.filters ?? []), ...presetFilters, ...segmentFilters];

    const viewFilterHash = Enumerable.from(view?.filters || []).toDictionary(f => getSafeId(f), f => f);
    const requestFilterHash = Enumerable.from(mergedRequestFilters).toDictionary(f => getSafeId(f), f => f);

    const effectiveFilters = [];

    // Same-field request conditions the filters list cannot express alongside the view's filter
    // (one slot per field downstream): the view filter keeps the slot and these are ANDed on via
    // advancedFilter below — FilterCompiler merges filters and advancedFilter with AND.
    const overflowRequestFilters: EntityViewFilter[] = [];

    // Only allow-list shapes can be merged by value intersection; any other operator pairing
    // (notIn, notEquals, isNotEmpty, contains, between, ...) must keep both conditions.
    const intersectableOperators = new Set([EntityAdvancedFilterOperator.Equals, EntityAdvancedFilterOperator.In]);
    const isIntersectable = (filter: EntityViewFilter) => !filter.operator || intersectableOperators.has(filter.operator);

    for (const f of metadata.systemFilters) {

            // Boards address custom fields by storage path (customFields.<fieldId>), never by the
            // mapping id in _id, so the path is a third lookup key.
            const lookup = (hash: Enumerable.IDictionary<string, EntityViewFilter>) => hash.get(f?.attributeFilterName ?? f._id) ?? hash.get(f._id) ?? (f.path ? hash.get(f.path) : undefined);
            const viewFilter = lookup(viewFilterHash);
            const requestFilter = lookup(requestFilterHash);

            let effectiveFilter = requestFilter || viewFilter;

            // If both exist, a segment replaces the view field (pill switch). Otherwise the view
            // is a hard baseline the request can only narrow. Allow-lists narrow by intersection;
            // every pairing the intersection cannot express keeps both, ANDed via advancedFilter.
            // Sentinel note: Me is resolved to the real user by the strategy BEFORE this merge
            // (resolveMeFilterPlaceholders on both view and request filters), so a "My ..." view
            // arrives here as an ordinary owner allow-list; an unresolved Me intersects to
            // disjoint and fails closed rather than letting the request escape.
            if (requestFilter && viewFilter && (viewFilter._id === requestFilter._id)) {

                if (segmentFieldIds.has(getSafeId(requestFilter))) {
                    effectiveFilter = requestFilter;
                } else {
                    // Wrap scalars so single-value view filters (e.g. status: 'Cancelled') get the
                    // same subset enforcement as multi-select arrays.
                    const normalize = (value: any) => Array.isArray(value) ? value : [value];

                    // Entity refs compare by _id, primitives by value. Other object shapes (date
                    // ranges, relative-date methods) have no reliable identity to intersect on.
                    const comparableKey = (value: any) => {
                        if (value === null || value === undefined) return undefined;
                        if (typeof value !== 'object') return value;
                        return '_id' in value ? value._id : undefined;
                    };

                    const viewValues = normalize(viewFilter.value);
                    const requestValues = normalize(requestFilter.value);
                    const comparable = viewValues.concat(requestValues).every(v => comparableKey(v) !== undefined);

                    const keepBoth = () => {
                        effectiveFilter = viewFilter;
                        overflowRequestFilters.push(requestFilter);
                    };

                    if (!isIntersectable(viewFilter) || !isIntersectable(requestFilter) || !comparable) {
                        // Intersection would read an exclusion (notIn/notEquals) as an allow-list —
                        // inverting the view — or drop the view baseline under a non-list request
                        // (isNotEmpty, contains, between) or identity-less values.
                        keepBoth();
                    } else if (viewValues.length > 0) {
                        const viewValueSet = new Set(viewValues.map(comparableKey));
                        const allowedRequestValues = requestValues.filter(v => viewValueSet.has(comparableKey(v)));

                        if (allowedRequestValues.length === requestValues.length) {
                            effectiveFilter = requestFilter;
                        } else if (allowedRequestValues.length > 0) {
                            effectiveFilter = { ...requestFilter, value: allowedRequestValues as EntityFilterValue };
                        } else {
                            // Disjoint selection must return no rows — but `in []` is discarded
                            // downstream (hasValue), which would UNCONSTRAIN the field instead.
                            // AND the disjoint conditions so the empty set compiles for real.
                            keepBoth();
                        }
                    }
                }
            }

            // Pill owns the filters slot (last write in the merged dictionary). The chip is a second
            // condition on that field, so it cannot sit in `filters` — AND it, unless it is the leftover
            // All allow-list. `in []` is not used: a disjoint chip ANDs to no rows instead of unconstraining.
            const userFilter = userByField.get(f?.attributeFilterName ?? f._id) ?? userByField.get(f._id) ?? (f.path ? userByField.get(f.path) : undefined);
            if (userFilter && segmentFieldIds.has(getSafeId(userFilter))) {
                const segmentFilter = segmentByField.get(getSafeId(userFilter));
                const defaultFilter = defaultByField.get(getSafeId(userFilter));
                if (segmentFilter && shouldAndSegmentChip(userFilter, segmentFilter, defaultFilter)) {
                    overflowRequestFilters.push(userFilter);
                }
            }

            if (effectiveFilter) {
                effectiveFilters.push(effectiveFilter);
            }
        }

    // Preserve __advancedFilter from request filters if not already on request.advancedFilter
    const embeddedAdvancedFilter = requestFilterHash.contains('__advancedFilter')
        ? requestFilterHash.get('__advancedFilter')?.value as unknown as EntityAdvancedFilterGroup
        : null;

    // AND the same-field overflow conditions onto the effective advancedFilter. An Or root is
    // preserved as a nested group so its disjunction is not flattened into the AND.
    const baseAdvancedFilter = request?.advancedFilter ?? embeddedAdvancedFilter ?? view?.advancedFilter;
    const overflowRules = overflowRequestFilters.map(f => ({ fieldId: f.fieldId ?? f._id, operator: f.operator ?? EntityAdvancedFilterOperator.Equals, value: f.value } as EntityAdvancedFilterConditionLoose as unknown as EntityAdvancedFilterNode));
    const effectiveAdvancedFilter = !overflowRules.length
        ? baseAdvancedFilter
        : !baseAdvancedFilter?.rules?.length
            ? { operator: EntityAdvancedFilterLogicalOperator.And, rules: overflowRules }
            : baseAdvancedFilter.operator === EntityAdvancedFilterLogicalOperator.Or
                ? { operator: EntityAdvancedFilterLogicalOperator.And, rules: [baseAdvancedFilter, ...overflowRules] }
                : { ...baseAdvancedFilter, rules: [...baseAdvancedFilter.rules, ...overflowRules] };

    const unpaged = !!request?.unpaged;
    const rawPageSize = request?.pageSize ?? view?.pageSize ?? DEFAULT_PAGE_SIZE;
    // Unpaged: page 0; return up to UNPAGED_MAX_RECORDS (ignore view pageSize — use request override or max)
    // `unpaged` is a cap, never "everything": the page size is clamped to UNPAGED_MAX_RECORDS, so a
    // caller asking for an unbounded set silently gets a truncated one. Calendar boards send
    // `unpaged` together with `range`, and it is the range — narrowed at the filter compiler by
    // buildCalendarRangeFilter — that makes the read genuinely bounded. A Calendar fetch without a
    // range is bounded only by this cap and will quietly drop rows once a tenant exceeds it.
    const page = unpaged ? 0 : (request?.page ?? 0);
    const pageSize = unpaged
        ? Math.min(
            request?.pageSize && request.pageSize > 0 ? request.pageSize : UNPAGED_MAX_RECORDS,
            UNPAGED_MAX_RECORDS,
        )
        : rawPageSize;

    const effectiveBase = {
        search: request?.search?.trim(),
        filters: effectiveFilters,
        advancedFilter: effectiveAdvancedFilter,
        advancedFilterStrategy: request?.advancedFilterStrategy ?? view?.advancedFilterStrategy,
        entityIds: request?.entityIds,
        parentId: request?.parentId ?? null,
        parentEntity: request?.parentEntity ?? null,
        companyIds: request?.companyIds ?? [],
        sort: request?.sort ?? view?.sort,
        page,
        pageSize,
        unpaged,
        // Calendar visible range. Carried through untouched — the range narrows the query at the
        // filter-compiler layer (buildCalendarRangeFilter), not by merging into `filters`.
        range: request?.range,
        // Keyset position and snapshot token are the reader's own bookkeeping — carried through
        // untouched. Deriving or clamping either here would break the walk it belongs to.
        after: request?.after,
        readToken: request?.readToken,
        skipCount: !!request?.skipCount,
        groupBy: request?.groupBy,
        subGroupBy: request?.subGroupBy,
        isEmbedded: request?.isEmbedded,
        variant: request?.variant,
        timezone: request?.timezone,
        presetId: request?.presetId,
        includePresetCounts: request?.includePresetCounts,
        segmentId: request?.segmentId,
    };

    return request?.version === 2
        ? { ...effectiveBase, version: 2, skipProjection: request?.skipProjection }
        : { ...effectiveBase, version: request?.version };
}

export function getViewRequestOptions(request: EntityItemsRequest): EntityViewRequestOptions {
    const options: EntityViewRequestOptions = {};
    if (request?.variant) {
        options.variant = request.variant;
    }
    if (request?.isEmbedded) {
        options.type = EntityViewType.Embedded;
    }
    return options;
}
